import type { Camera } from '../../services/camerasApi';
import type { Policy } from '../../services/policiesApi';
import { resolveConfiguredCameraLabel } from '../../domain/customerLabels/customerFacingControlLabel';

export type BehaviorMode = 'off' | 'dwell' | 'loiter';
export type BehaviorSensitivity = 'low' | 'medium' | 'high';
export type OccupancyMode = 'off' | 'occupancy';
export type WorkerDownMode = 'off' | 'worker_down';
export type ForkliftProximityMode = 'off' | 'proximity';
export type PedestrianNearMovingForkliftMode = 'off' | 'near_moving';
export type ForkliftSpeedingMode = 'off' | 'speeding';
export type ZoneIntrusionMode = 'off' | 'restricted' | 'warning';
export type LineCrossingMode = 'off' | 'crossing';
export type RegionTransitionMode = 'off' | 'entrance' | 'exiting';

export const BEHAVIOR_SENSITIVITY_OPTIONS: Array<{ value: BehaviorSensitivity; label: string; helper: string }> = [
  { value: 'low', label: 'Low', helper: 'Fewer alerts' },
  { value: 'medium', label: 'Medium', helper: 'Balanced' },
  { value: 'high', label: 'High', helper: 'Faster alerts' },
];

export const DWELL_PRESET_DEFAULTS: Record<BehaviorSensitivity, { threshold: number; persistence: number; cooldown: number }> = {
  low: { threshold: 120, persistence: 5, cooldown: 180 },
  medium: { threshold: 60, persistence: 3, cooldown: 120 },
  high: { threshold: 20, persistence: 2, cooldown: 60 },
};

export const LOITER_PRESET_DEFAULTS: Record<
  BehaviorSensitivity,
  { threshold: number; persistence: number; cooldown: number; toleranceLabel: string }
> = {
  low: { threshold: 90, persistence: 5, cooldown: 300, toleranceLabel: 'strict movement tolerance' },
  medium: { threshold: 45, persistence: 3, cooldown: 180, toleranceLabel: 'balanced movement tolerance' },
  high: { threshold: 20, persistence: 2, cooldown: 120, toleranceLabel: 'permissive movement tolerance' },
};

export const OCCUPANCY_PRESET_DEFAULTS: Record<BehaviorSensitivity, { persistence: number; cooldown: number; clearHysteresisSamples: number }> = {
  low: { persistence: 10, cooldown: 180, clearHysteresisSamples: 2 },
  medium: { persistence: 5, cooldown: 90, clearHysteresisSamples: 2 },
  high: { persistence: 2, cooldown: 45, clearHysteresisSamples: 1 },
};

export const FORKLIFT_PROXIMITY_PRESET_DEFAULTS: Record<
  BehaviorSensitivity,
  { distanceThresholdNorm: number; persistence: number; cooldown: number; refresh: number }
> = {
  low: { distanceThresholdNorm: 0.14, persistence: 3, cooldown: 120, refresh: 15 },
  medium: { distanceThresholdNorm: 0.12, persistence: 2, cooldown: 90, refresh: 10 },
  high: { distanceThresholdNorm: 0.1, persistence: 1, cooldown: 45, refresh: 5 },
};

export const PEDESTRIAN_NEAR_MOVING_FORKLIFT_PRESET_DEFAULTS: Record<
  BehaviorSensitivity,
  { distanceThresholdNorm: number; persistence: number; cooldown: number; refresh: number; minMotionNorm: number }
> = {
  low: { distanceThresholdNorm: 0.14, persistence: 2, cooldown: 120, refresh: 15, minMotionNorm: 0.02 },
  medium: { distanceThresholdNorm: 0.12, persistence: 1.5, cooldown: 90, refresh: 10, minMotionNorm: 0.01 },
  high: { distanceThresholdNorm: 0.1, persistence: 1, cooldown: 45, refresh: 5, minMotionNorm: 0.005 },
};

export const FORKLIFT_SPEEDING_PRESET_DEFAULTS: Record<
  BehaviorSensitivity,
  { speedThresholdNorm: number; persistence: number; cooldown: number; refresh: number }
> = {
  low: { speedThresholdNorm: 0.04, persistence: 3, cooldown: 120, refresh: 15 },
  medium: { speedThresholdNorm: 0.03, persistence: 2, cooldown: 90, refresh: 10 },
  high: { speedThresholdNorm: 0.02, persistence: 1, cooldown: 45, refresh: 5 },
};

export const ZONE_INTRUSION_PRESET_DEFAULTS: Record<
  Exclude<ZoneIntrusionMode, 'off'>,
  Record<BehaviorSensitivity, { dwellGate: number; cooldown: number; severityLabel: string }>
> = {
  restricted: {
    low: { dwellGate: 5, cooldown: 300, severityLabel: 'restricted-area urgency' },
    medium: { dwellGate: 2, cooldown: 180, severityLabel: 'restricted-area urgency' },
    high: { dwellGate: 1, cooldown: 90, severityLabel: 'restricted-area urgency' },
  },
  warning: {
    low: { dwellGate: 10, cooldown: 300, severityLabel: 'warning-level urgency' },
    medium: { dwellGate: 5, cooldown: 180, severityLabel: 'warning-level urgency' },
    high: { dwellGate: 2, cooldown: 90, severityLabel: 'warning-level urgency' },
  },
};

export const LINE_CROSSING_PRESET_DEFAULTS: Record<BehaviorSensitivity, { cooldown: number; jitterLabel: string; movementLabel: string }> = {
  low: { cooldown: 180, jitterLabel: 'stronger line-edge damping', movementLabel: 'higher movement requirement' },
  medium: { cooldown: 90, jitterLabel: 'balanced line-edge damping', movementLabel: 'balanced movement requirement' },
  high: { cooldown: 45, jitterLabel: 'lighter line-edge damping', movementLabel: 'faster crossing recognition' },
};

export const REGION_TRANSITION_PRESET_DEFAULTS: Record<BehaviorSensitivity, { cooldown: number; dampingLabel: string }> = {
  low: { cooldown: 120, dampingLabel: 'stronger boundary damping' },
  medium: { cooldown: 60, dampingLabel: 'balanced boundary damping' },
  high: { cooldown: 20, dampingLabel: 'faster transition eventing' },
};

export const isBehaviorPolicyType = (policyType: string): boolean =>
  policyType === 'dwell_violation' || policyType === 'loitering';

export const usesManagedTimingPolicyType = (policyType: string): boolean =>
  [
    'dwell_violation',
    'loitering',
    'occupancy',
    'worker_down',
    'forklift_proximity',
    'pedestrian_near_moving_forklift',
    'forklift_speeding',
    'zone_intrusion',
    'region_entrance',
    'region_exiting',
  ].includes(policyType);

const defaultBehaviorModeForPolicy = (policyType: string): BehaviorMode =>
  policyType === 'loitering' ? 'loiter' : 'dwell';

const allowedBehaviorModesForPolicy = (policyType: string): BehaviorMode[] =>
  policyType === 'loitering' ? ['off', 'loiter'] : ['off', 'dwell'];

export const readBehaviorRuleUiState = (
  policyType: string,
  config: Record<string, any> | undefined,
): {
  mode: BehaviorMode;
  sensitivity: BehaviorSensitivity;
  thresholdOverrideSeconds: string;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const nested =
    cfg.behavior_rule && typeof cfg.behavior_rule === 'object' && !Array.isArray(cfg.behavior_rule) ? cfg.behavior_rule : {};
  const allowedModes = allowedBehaviorModesForPolicy(policyType);
  const rawMode = String(nested.mode ?? cfg.behavior_mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(nested.sensitivity ?? cfg.behavior_sensitivity ?? '').trim().toLowerCase();
  const thresholdValue =
    nested.threshold_override_seconds ??
    cfg.threshold_override_seconds ??
    (policyType === 'dwell_violation' ? cfg.max_dwell_seconds : cfg.max_loiter_seconds);
  const mode = (allowedModes.includes(rawMode as BehaviorMode) ? rawMode : defaultBehaviorModeForPolicy(policyType)) as BehaviorMode;
  const sensitivity = (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity;
  return {
    mode,
    sensitivity,
    thresholdOverrideSeconds:
      thresholdValue === null || thresholdValue === undefined || thresholdValue === '' ? '' : String(thresholdValue),
  };
};

export const applyBehaviorRuleUiState = (
  policyType: string,
  config: Record<string, any> | undefined,
  uiState: {
    mode: BehaviorMode;
    sensitivity: BehaviorSensitivity;
    thresholdOverrideSeconds: string;
  },
): Record<string, any> => {
  const next: Record<string, any> = { ...(config || {}) };
  const existingBehaviorRule =
    next.behavior_rule && typeof next.behavior_rule === 'object' && !Array.isArray(next.behavior_rule)
      ? { ...next.behavior_rule }
      : {};

  const behaviorRule: Record<string, any> = {
    ...existingBehaviorRule,
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  };

  if (uiState.thresholdOverrideSeconds === '') {
    delete behaviorRule.threshold_override_seconds;
  } else {
    const threshold = Number(uiState.thresholdOverrideSeconds);
    if (Number.isFinite(threshold)) {
      behaviorRule.threshold_override_seconds = threshold;
    }
  }

  next.behavior_rule = behaviorRule;
  delete next.behavior_mode;
  delete next.behavior_sensitivity;
  delete next.threshold_override_seconds;
  delete next.mode;
  delete next.sensitivity;
  if (policyType === 'dwell_violation') {
    delete next.max_dwell_seconds;
  }
  if (policyType === 'loitering') {
    delete next.max_loiter_seconds;
    delete next.stationary_eps_norm;
  }
  return next;
};

export const describeBehaviorPreset = (policyType: string, sensitivity: BehaviorSensitivity): string => {
  if (policyType === 'loitering') {
    const preset = LOITER_PRESET_DEFAULTS[sensitivity];
    return `Best for lingering or suspicious presence: it alerts when someone remains in the area for about ${preset.threshold}s, then waits ${preset.cooldown}s before opening a new incident. ${preset.toleranceLabel.charAt(0).toUpperCase() + preset.toleranceLabel.slice(1)} helps avoid flagging normal pass-through movement.`;
  }
  const preset = DWELL_PRESET_DEFAULTS[sensitivity];
  return `Best when time spent inside the zone is the problem: it alerts after about ${preset.threshold}s in the area, then waits ${preset.cooldown}s before opening a new incident.`;
};

const readNestedBehaviorRule = (config: Record<string, any> | undefined): Record<string, any> => {
  const cfg = config && typeof config === 'object' ? config : {};
  return cfg.behavior_rule && typeof cfg.behavior_rule === 'object' && !Array.isArray(cfg.behavior_rule) ? cfg.behavior_rule : {};
};

const writeNestedBehaviorRule = (config: Record<string, any> | undefined, behaviorRule: Record<string, any>): Record<string, any> => {
  const next: Record<string, any> = { ...(config || {}) };
  next.behavior_rule = behaviorRule;
  delete next.behavior_mode;
  delete next.behavior_sensitivity;
  delete next.mode;
  delete next.sensitivity;
  return next;
};

export const readOccupancyRuleUiState = (config: Record<string, any> | undefined): {
  mode: OccupancyMode;
  sensitivity: BehaviorSensitivity;
  maxOccupancy: string;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  const maxOccupancy = cfg.max_occupancy ?? cfg.max_count ?? behavior.max_occupancy;
  return {
    mode: (rawMode === 'off' || rawMode === 'occupancy' ? rawMode : 'occupancy') as OccupancyMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
    maxOccupancy: maxOccupancy === null || maxOccupancy === undefined || maxOccupancy === '' ? '' : String(maxOccupancy),
  };
};

export const readWorkerDownRuleUiState = (config: Record<string, any> | undefined): {
  mode: WorkerDownMode;
  sensitivity: BehaviorSensitivity;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  return {
    mode: (rawMode === 'off' || rawMode === 'worker_down' ? rawMode : 'worker_down') as WorkerDownMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const readForkliftProximityRuleUiState = (config: Record<string, any> | undefined): {
  mode: ForkliftProximityMode;
  sensitivity: BehaviorSensitivity;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  return {
    mode: (rawMode === 'off' || rawMode === 'proximity' ? rawMode : 'proximity') as ForkliftProximityMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const readPedestrianNearMovingForkliftRuleUiState = (config: Record<string, any> | undefined): {
  mode: PedestrianNearMovingForkliftMode;
  sensitivity: BehaviorSensitivity;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  return {
    mode: (rawMode === 'off' || rawMode === 'near_moving' ? rawMode : 'near_moving') as PedestrianNearMovingForkliftMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const readForkliftSpeedingRuleUiState = (config: Record<string, any> | undefined): {
  mode: ForkliftSpeedingMode;
  sensitivity: BehaviorSensitivity;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  return {
    mode: (rawMode === 'off' || rawMode === 'speeding' ? rawMode : 'speeding') as ForkliftSpeedingMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const applyForkliftProximityRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: ForkliftProximityMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const applyPedestrianNearMovingForkliftRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: PedestrianNearMovingForkliftMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const applyForkliftSpeedingRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: ForkliftSpeedingMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const applyWorkerDownRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: WorkerDownMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const describeForkliftProximityPreset = (sensitivity: BehaviorSensitivity): string => {
  const preset = FORKLIFT_PROXIMITY_PRESET_DEFAULTS[sensitivity];
  return `Best for general warehouse areas: alerts when a person stays dangerously close to a forklift for about ${preset.persistence}s. After they separate, it waits ${preset.cooldown}s before opening a new incident, and while the risk continues it refreshes the same incident about every ${preset.refresh}s instead of creating duplicates.`;
};

export const describePedestrianNearMovingForkliftPreset = (sensitivity: BehaviorSensitivity): string => {
  const preset = PEDESTRIAN_NEAR_MOVING_FORKLIFT_PRESET_DEFAULTS[sensitivity];
  return `Best when you only want alerts for active traffic: it fires when a person stays too close to a forklift that is actually moving for about ${preset.persistence}s. After the forklift stops or the pair separates, it waits ${preset.cooldown}s before opening a new incident, and while the risk continues it refreshes the same incident about every ${preset.refresh}s.`;
};

export const describeForkliftSpeedingPreset = (sensitivity: BehaviorSensitivity): string => {
  const preset = FORKLIFT_SPEEDING_PRESET_DEFAULTS[sensitivity];
  return `Best for spotting aggressive driving patterns: it alerts when a forklift appears to be moving unusually fast in this camera view for about ${preset.persistence}s. This uses camera-relative motion, not a calibrated site speed limit. After it slows down, it waits ${preset.cooldown}s before opening a new incident, and while the behavior continues it refreshes the same incident about every ${preset.refresh}s.`;
};

export const applyOccupancyRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: OccupancyMode; sensitivity: BehaviorSensitivity; maxOccupancy: string },
): Record<string, any> => {
  const behaviorRule: Record<string, any> = {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  };
  const next = writeNestedBehaviorRule(config, behaviorRule);
  if (uiState.maxOccupancy === '') {
    delete next.max_occupancy;
    delete next.max_count;
  } else {
    const maxOccupancy = Number(uiState.maxOccupancy);
    if (Number.isFinite(maxOccupancy)) {
      next.max_occupancy = maxOccupancy;
    }
  }
  return next;
};

export const describeOccupancyPreset = (sensitivity: BehaviorSensitivity): string => {
  const preset = OCCUPANCY_PRESET_DEFAULTS[sensitivity];
  return `Best for crowding control in break rooms, cages, or staging areas: it alerts when the people count stays above the limit for about ${preset.persistence}s. After the area clears, it waits ${preset.cooldown}s before opening a new incident.`;
};

export const describeWorkerDownPreset = (sensitivity: BehaviorSensitivity): string => {
  if (sensitivity === 'low') {
    return 'Best when false positives are expensive: use stronger damping and longer confirmation before opening a review for a possibly down worker.';
  }
  if (sensitivity === 'high') {
    return 'Best when faster recognition matters most: use lighter damping so a clearly down and motionless worker reaches review sooner, with more need for camera-placement discipline.';
  }
  return 'Best for balanced Worker Down staging: combine posture and low-motion evidence with enough confirmation to avoid opening incidents on brief crouches, bends, or partial-body views.';
};

export const readZoneIntrusionRuleUiState = (config: Record<string, any> | undefined): {
  mode: ZoneIntrusionMode;
  sensitivity: BehaviorSensitivity;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  return {
    mode: (['off', 'restricted', 'warning'].includes(rawMode) ? rawMode : 'restricted') as ZoneIntrusionMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const applyZoneIntrusionRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: ZoneIntrusionMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const describeZoneIntrusionPreset = (mode: ZoneIntrusionMode, sensitivity: BehaviorSensitivity): string => {
  if (mode === 'off') return 'Zone intrusion is disabled.';
  const preset = ZONE_INTRUSION_PRESET_DEFAULTS[mode][sensitivity];
  return `Best for keeping the selected subject out of the chosen zones: it alerts after about ${preset.dwellGate}s inside the area, then waits ${preset.cooldown}s before opening a new incident. Use Restricted for no-go areas and Warning for caution areas where you still want operator review.`;
};

export const readLineCrossingRuleUiState = (config: Record<string, any> | undefined): {
  mode: LineCrossingMode;
  sensitivity: BehaviorSensitivity;
} => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  return {
    mode: (['off', 'crossing'].includes(rawMode) ? rawMode : 'crossing') as LineCrossingMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const applyLineCrossingRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: LineCrossingMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const describeLineCrossingPreset = (sensitivity: BehaviorSensitivity): string => {
  const preset = LINE_CROSSING_PRESET_DEFAULTS[sensitivity];
  return `Best for tripwires, stop lines, and one-way boundaries: sensitivity balances fast crossing recognition against false alerts from edge jitter. After a crossing, it waits ${preset.cooldown}s before opening another incident.`;
};

export const readRegionTransitionRuleUiState = (
  policyType: string,
  config: Record<string, any> | undefined,
): { mode: RegionTransitionMode; sensitivity: BehaviorSensitivity } => {
  const cfg = config && typeof config === 'object' ? config : {};
  const behavior = readNestedBehaviorRule(cfg);
  const rawMode = String(behavior.mode ?? cfg.behavior_mode ?? cfg.mode ?? '').trim().toLowerCase();
  const rawSensitivity = String(behavior.sensitivity ?? cfg.behavior_sensitivity ?? cfg.sensitivity ?? '').trim().toLowerCase();
  const defaultMode = policyType === 'region_exiting' ? 'exiting' : 'entrance';
  return {
    mode: (['off', 'entrance', 'exiting'].includes(rawMode) ? rawMode : defaultMode) as RegionTransitionMode,
    sensitivity: (['low', 'medium', 'high'].includes(rawSensitivity) ? rawSensitivity : 'medium') as BehaviorSensitivity,
  };
};

export const applyRegionTransitionRuleUiState = (
  config: Record<string, any> | undefined,
  uiState: { mode: RegionTransitionMode; sensitivity: BehaviorSensitivity },
): Record<string, any> =>
  writeNestedBehaviorRule(config, {
    ...readNestedBehaviorRule(config),
    mode: uiState.mode,
    sensitivity: uiState.sensitivity,
  });

export const describeRegionTransitionPreset = (sensitivity: BehaviorSensitivity): string => {
  const preset = REGION_TRANSITION_PRESET_DEFAULTS[sensitivity];
  return `Best for tracking clear entries or exits across a boundary: sensitivity balances faster transition recognition against duplicate edge events. After a transition, it waits ${preset.cooldown}s before opening another incident.`;
};

export const describePolicyUseCase = (policyAlias: string | undefined, policyType: string): string | null => {
  const alias = String(policyAlias || '').trim();
  const type = String(policyType || '').trim();

  if (alias === 'ppe_compliance' || type === 'ppe_compliance') {
    return 'Use this to confirm workers are wearing required PPE in the selected camera view. It works best at entrances, task stations, and controlled work areas where gear expectations are consistent.';
  }

  if (alias === 'zone_intrusion') {
    return 'Use this to alert when a person enters a protected area. It is best for doors, cages, and spaces where access itself is the safety concern.';
  }
  if (alias === 'pedestrian_in_forklift_zone') {
    return 'Use this to catch people entering forklift-only lanes or work cells. It is best for shared warehouses where pedestrian separation is the main goal.';
  }
  if (alias === 'forklift_in_pedestrian_zone') {
    return 'Use this to catch forklifts entering walkways or pedestrian-priority space. It is best for protecting break areas, crossings, and marked pedestrian routes.';
  }
  if (alias === 'forklift_in_restricted_area') {
    return 'Use this to catch forklifts entering no-go or restricted areas. It is best for docks, hazard zones, and spaces where vehicle access should be rare or prohibited.';
  }
  if (type === 'zone_intrusion') {
    return 'Use this to alert when the selected subject enters a protected area. It is best when access itself is the safety concern.';
  }

  if (alias === 'line_crossing') {
    return 'Use this to alert when a person crosses a virtual tripwire. It is best for perimeter lines, one-way passages, and doorway-style monitoring.';
  }
  if (alias === 'forklift_line_crossing') {
    return 'Use this to alert when a forklift crosses a virtual line. It is best for stop lines, lane boundaries, and checkpoints where vehicle path matters.';
  }
  if (type === 'line_crossing') {
    return 'Use this to alert when the selected subject crosses a virtual line. It is best for directional boundaries and checkpoint-style monitoring.';
  }

  if (alias === 'dwell_violation') {
    return 'Use this when simply staying in an area too long is the risk. It is best for exits, loading areas, and spaces that should stay clear.';
  }
  if (alias === 'forklift_dwell') {
    return 'Use this when a forklift should not remain parked or idle in a zone for long. It is best for fire lanes, transfer points, and areas that should keep moving.';
  }
  if (type === 'dwell_violation') {
    return 'Use this when time spent inside the zone is the risk, even if nothing else unusual happens.';
  }

  if (alias === 'loitering' || type === 'loitering') {
    return 'Use this to catch lingering behavior rather than simple pass-through traffic. It is best for entrances, cages, and spaces where someone hanging around should be reviewed.';
  }

  if (alias === 'occupancy' || type === 'occupancy') {
    return 'Use this to limit crowding in a room or work area. It is best for break rooms, cages, staging zones, and any space with a safe people-count limit.';
  }

  if (alias === 'worker_down' || type === 'worker_down') {
    return 'Use this to review situations where a person appears down and largely immobile for long enough to require attention. It is best for worker-protection cameras with posture-friendly viewpoints and clear full-body coverage.';
  }

  if (alias === 'region_entrance' || type === 'region_entrance') {
    return 'Use this to alert on entries into a monitored region. It is best when direction matters more than simple presence inside the zone.';
  }
  if (alias === 'region_exiting' || type === 'region_exiting') {
    return 'Use this to alert on exits from a monitored region. It is best for tracking departures from controlled spaces or flow out of a work area.';
  }

  if (alias === 'forklift_proximity' || type === 'forklift_proximity') {
    return 'Use this to flag sustained unsafe spacing between a pedestrian and a forklift in the same camera view. It is best for shared aisles, docks, and congestion points.';
  }
  if (alias === 'pedestrian_near_moving_forklift' || type === 'pedestrian_near_moving_forklift') {
    return 'Use this when the main concern is people getting too close to forklifts that are actively traveling. It is best for live traffic lanes where moving interactions matter most.';
  }
  if (alias === 'forklift_speeding' || type === 'forklift_speeding') {
    return 'Use this to flag forklifts that appear to be moving unusually fast in this camera view. It helps surface risky driving behavior, not enforce a posted speed limit.';
  }

  return null;
};

export const PPE_ITEM_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'helmet', label: 'Helmet' },
  { value: 'vest', label: 'Vest' },
  { value: 'gloves', label: 'Gloves' },
  { value: 'goggles', label: 'Goggles' },
  { value: 'boots', label: 'Boots' },
  { value: 'mask', label: 'Mask' },
];

export const DEFAULT_PPE_REQUIRED_ITEMS = ['helmet', 'vest'];

export const SEVERITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export const DAYS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

export type EdgeSupport = 'supported' | 'partial' | 'not_supported';
export type CameraProfileId = 'A' | 'B' | 'C' | 'D';

export const getWarehouseEdgeSupport = (
  policyType: string,
  cameraProfileId?: CameraProfileId | string | null,
): { support: EdgeSupport; message?: string } => {
  const t = String(policyType || '').trim();
  const profileId = String(cameraProfileId || '').trim().toUpperCase();
  if (t === 'worker_down') {
    if (!profileId) {
      return {
        support: 'partial',
        message:
          'Worker Down is being staged for Worker Protection cameras. Select a Profile B camera and complete Epic 52 runtime and validation gates before relying on it operationally.',
      };
    }
    if (profileId !== 'B') {
      return {
        support: 'not_supported',
        message:
          `Worker Down is intended for a Worker Protection Camera (Profile B). The selected camera is Profile ${profileId}, so posture-sensitive compatibility is not guaranteed.`,
      };
    }
    return {
      support: 'partial',
      message:
        'Worker Down is staged for Worker Protection cameras, but rollout still depends on runtime completion, evidence semantics, and shadow-validation gates before operational use.',
    };
  }
  const requiresProfileC = ['forklift_proximity', 'pedestrian_near_moving_forklift', 'forklift_speeding'].includes(t);
  if (requiresProfileC && !profileId) {
    return {
      support: 'partial',
      message:
        'Wave 2 forklift interaction-risk policies are intended for Forklift ROI Safety cameras. Select a Profile C camera to confirm runtime compatibility before deploy.',
    };
  }
  if (requiresProfileC && profileId !== 'C') {
    return {
      support: 'not_supported',
      message:
        `This Wave 2 forklift policy requires a Forklift ROI Safety Camera (Profile C). The selected camera is Profile ${profileId}, so runtime compatibility is not guaranteed.`,
    };
  }
  if (t === 'pedestrian_near_moving_forklift') {
    return {
      support: 'supported',
      message:
        'Requires same-camera person and forklift tracking plus stable forklift motion estimates. Validate on representative moving-forklift replay before relying on it in pilot signoff.',
    };
  }
  if (t === 'forklift_proximity') {
    return {
      support: 'supported',
      message:
        'Supports sustained same-camera pedestrian-to-forklift spacing alerts. Best for shared lanes, loading bays, and congestion points where unsafe spacing matters even when the forklift is slow or paused.',
    };
  }
  if (t === 'forklift_speeding') {
    return {
      support: 'supported',
      message:
        'Uses threshold-based relative motion monitoring, not calibrated legal-speed measurement. Validate smoothing and site-specific thresholds before making speed-enforcement claims.',
    };
  }
  if (['ppe_compliance', 'zone_intrusion', 'dwell_violation', 'loitering', 'line_crossing', 'occupancy', 'worker_down', 'forklift_proximity', 'pedestrian_near_moving_forklift', 'forklift_speeding', 'region_entrance', 'region_exiting'].includes(t)) {
    return { support: 'supported' };
  }
  return { support: 'partial', message: 'Edge execution support for this policy type is unknown; verify before relying on it.' };
};

export const formatPolicySubjectLabel = (value: string): string => {
  if (value === 'all') return 'All entities';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const OUTBOUND_POLICY_ACTION_TYPES = new Set(['email.notify', 'sms.notify', 'genetec.notify', 'webhook.call']);

export function stripOutboundPolicyActions(actions: unknown): any[] {
  return (Array.isArray(actions) ? actions : []).filter(
    (a: any) => !OUTBOUND_POLICY_ACTION_TYPES.has(String(a?.type || '').trim()),
  );
}

export function getPolicyActionByType(actions: unknown, actionType: string): any | undefined {
  const t = String(actionType || '').trim();
  return (Array.isArray(actions) ? actions : []).find((a: any) => String(a?.type || '').trim() === t);
}

export function otherOutboundThan(actions: unknown, excludeType: string): any[] {
  const ex = String(excludeType || '').trim();
  return (Array.isArray(actions) ? actions : []).filter(
    (a: any) =>
      OUTBOUND_POLICY_ACTION_TYPES.has(String(a?.type || '').trim()) && String(a?.type || '').trim() !== ex,
  );
}

export function truthyParamFlag(v: unknown): boolean {
  if (v === true) return true;
  if (v === false || v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes' || s === 'on';
}

export function canonicalPatternPayload(opts: {
  enabled: boolean;
  minEvents?: number;
  windowMinutes?: number;
  cooldownHours?: number;
}): { enabled: boolean; min_events: number; window_seconds: number; cooldown_seconds: number } | undefined {
  if (!opts.enabled) return undefined;
  const minEvents = Number(opts.minEvents ?? 5);
  const windowMinutes = Number(opts.windowMinutes ?? 60);
  const cooldownHours = Number(opts.cooldownHours ?? 4);
  return {
    enabled: true,
    min_events: minEvents,
    window_seconds: windowMinutes * 60,
    cooldown_seconds: cooldownHours * 3600,
  };
}

export const PATTERN_ALERT_POLICY_TYPES = [
  'zone_intrusion',
  'ppe_compliance',
  'forklift_proximity',
  'pedestrian_near_moving_forklift',
] as const;

export const COMPOSITE_POLICY_TYPE = 'composite_alert';

export const COMPOSITE_CONDITION_TYPES = [
  'RESTRICTED_AREA_ENTRY',
  'PPE_NON_COMPLIANCE',
  'PERSON_FORKLIFT_PROXIMITY',
] as const;

export type CompositeConditionType = (typeof COMPOSITE_CONDITION_TYPES)[number];
export type CompositeOperator = 'ALL' | 'ANY';
export type CompositeScopeBinding = 'SAME_ZONE' | 'SAME_CAMERA' | 'SAME_SITE';

export type CompositeTerm = {
  term_id: string;
  condition_type: CompositeConditionType;
  min_events: number;
  source_policy_id?: string;
};

export const COMPOSITE_CONDITION_BUYER_LABELS: Record<CompositeConditionType, string> = {
  RESTRICTED_AREA_ENTRY: 'Restricted-area entry',
  PPE_NON_COMPLIANCE: 'PPE non-compliance',
  PERSON_FORKLIFT_PROXIMITY: 'Forklift-pedestrian proximity',
};

export const COMPOSITE_PREVIEW_CONDITION_LABELS: Record<CompositeConditionType, string> = {
  RESTRICTED_AREA_ENTRY: 'RAE',
  PPE_NON_COMPLIANCE: 'PPE non-compliance',
  PERSON_FORKLIFT_PROXIMITY: 'Forklift-pedestrian proximity',
};

export const COMPOSITE_POLICY_TYPE_TO_CONDITION: Partial<Record<Policy['policy_type'], CompositeConditionType>> = {
  zone_intrusion: 'RESTRICTED_AREA_ENTRY',
  ppe_compliance: 'PPE_NON_COMPLIANCE',
  forklift_proximity: 'PERSON_FORKLIFT_PROXIMITY',
  pedestrian_near_moving_forklift: 'PERSON_FORKLIFT_PROXIMITY',
};

export type CompositeRuleV1 = {
  schema_version: 'composite_rule.v1';
  rule_id: string;
  version: number;
  name: string;
  enabled: boolean;
  operator: CompositeOperator;
  terms: CompositeTerm[];
  window_seconds: number;
  scope_binding: CompositeScopeBinding;
  cooldown_seconds: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  actions: Array<'email.notify'>;
};

const COMPOSITE_CONDITION_LABELS_SINGULAR: Record<CompositeConditionType, string> = {
  RESTRICTED_AREA_ENTRY: 'restricted-area entry',
  PPE_NON_COMPLIANCE: 'PPE non-compliance event',
  PERSON_FORKLIFT_PROXIMITY: 'forklift-pedestrian proximity event',
};

const COMPOSITE_CONDITION_LABELS_PLURAL: Record<CompositeConditionType, string> = {
  RESTRICTED_AREA_ENTRY: 'restricted-area entries',
  PPE_NON_COMPLIANCE: 'PPE non-compliance events',
  PERSON_FORKLIFT_PROXIMITY: 'forklift-pedestrian proximity events',
};

const COMPOSITE_SCOPE_LABELS: Record<CompositeScopeBinding, string> = {
  SAME_ZONE: 'same zone',
  SAME_CAMERA: 'same camera',
  SAME_SITE: 'same site',
};

const COMPOSITE_SCOPE_PREVIEW_LABELS: Record<CompositeScopeBinding, string> = {
  SAME_ZONE: 'Same zone',
  SAME_CAMERA: 'Same camera',
  SAME_SITE: 'Same site',
};

export const FORKLIFT_COMPOSITE_COMMISSIONING_COPY =
  'Forklift-pedestrian proximity is first-class in Composite and fail-closed. COMPOSITE_PRODUCTION_ALLOWED=NO until this condition is commissioned (EXPERIMENTAL_SHADOW).';

export const SAME_SITE_COMPOSITE_COMMISSIONING_COPY =
  'Same site is valid Composite vocabulary. Cross-device site correlation requires a convergent CONDITION_EVENT store and is currently fail-closed.';

export const COMPOSITE_AMBIGUOUS_EDGE_COPY =
  'Selected source policies belong to different edge devices. Composite cannot evaluate across devices in this runtime. Choose source policies on the same camera.';

export const COMPOSITE_AMBIGUOUS_CAMERA_COPY =
  'Selected source policies use different cameras. Same-camera Composite requires one shared camera.';

export const COMPOSITE_MISSING_SOURCE_TARGET_COPY =
  'A selected source policy is missing camera or edge identity. Choose a source policy with a known camera and edge device.';

export const COMPOSITE_RUNTIME_NOT_AVAILABLE_COPY =
  'Composite Alert runtime is not available in this phase. Rules may be saved as draft only and cannot be activated or deployed.';

const INTERNAL_ZONE_TOKEN_RE = /worksafety/i;

function looksLikeRawScopeId(value: string): boolean {
  const text = String(value || '').trim();
  if (!text) return true;
  if (INTERNAL_ZONE_TOKEN_RE.test(text)) return true;
  return /^(cam|pol|zone|dev)[_-]/i.test(text);
}

export function compositeConditionForPolicyType(policyType?: string | null): CompositeConditionType | null {
  const token = String(policyType || '').trim() as Policy['policy_type'];
  return COMPOSITE_POLICY_TYPE_TO_CONDITION[token] || null;
}

export function sourcePolicyCameraId(policy: Pick<Policy, 'scope' | 'camera_id' | 'target'>): string {
  const fromScope = Array.isArray(policy.scope?.camera_ids)
    ? String(policy.scope?.camera_ids[0] || '').trim()
    : '';
  return (
    fromScope
    || String(policy.target?.camera_id || '').trim()
    || String(policy.camera_id || '').trim()
  );
}

export function sourcePolicyEdgeDeviceId(
  policy: Pick<Policy, 'scope' | 'edge_device_id' | 'target'>,
): string {
  return (
    String(policy.target?.edge_device_id || '').trim()
    || String(policy.edge_device_id || '').trim()
    || String(policy.scope?.edge_device_id || '').trim()
  );
}

export function sourcePolicyZoneId(policy: Pick<Policy, 'scope'>): string {
  const fromScope = Array.isArray(policy.scope?.zone_ids)
    ? String(policy.scope?.zone_ids[0] || '').trim()
    : '';
  return fromScope;
}

export type SourcePolicySelectorFacts = {
  policy_id: string;
  condition_type: CompositeConditionType | null;
  edge_device_id: string;
  camera_id: string;
  zone_id: string;
  buyer_location: string;
};

export function sourcePolicySelectorFacts(
  policy: Policy,
  cameras: Array<Pick<Camera, 'camera_id' | 'name'>> = [],
): SourcePolicySelectorFacts {
  return {
    policy_id: String(policy.policy_id || '').trim(),
    condition_type: compositeConditionForPolicyType(policy.policy_type),
    edge_device_id: sourcePolicyEdgeDeviceId(policy),
    camera_id: sourcePolicyCameraId(policy),
    zone_id: sourcePolicyZoneId(policy),
    buyer_location: sourcePolicyLocationLabel(policy, cameras),
  };
}

export type CompositeDerivedTarget = { edge_device_id: string; camera_id: string };

export type CompositeTargetDerivation =
  | { ok: true; target: CompositeDerivedTarget | null }
  | { ok: false; error: string };

function uniqueNonEmpty(values: string[]): string[] {
  const out: string[] = [];
  for (const value of values) {
    const token = String(value || '').trim();
    if (token && !out.includes(token)) out.push(token);
  }
  return out;
}

export function deriveCompositeTarget(
  rule: CompositeRuleV1,
  policies?: Policy[],
): CompositeTargetDerivation {
  const boundIds = (rule.terms || [])
    .map((term) => String(term.source_policy_id || '').trim())
    .filter(Boolean);
  if (boundIds.length === 0) {
    return { ok: true, target: null };
  }
  const byId = new Map(
    (policies || []).map((policy) => [String(policy.policy_id || '').trim(), policy]),
  );
  const edges: string[] = [];
  const cameras: string[] = [];
  for (const policyId of boundIds) {
    const policy = byId.get(policyId);
    if (!policy) {
      return { ok: false, error: COMPOSITE_MISSING_SOURCE_TARGET_COPY };
    }
    const edge = sourcePolicyEdgeDeviceId(policy);
    const camera = sourcePolicyCameraId(policy);
    if (!edge || !camera) {
      return { ok: false, error: COMPOSITE_MISSING_SOURCE_TARGET_COPY };
    }
    edges.push(edge);
    cameras.push(camera);
  }
  const uniqueEdges = uniqueNonEmpty(edges);
  const uniqueCameras = uniqueNonEmpty(cameras);
  if (uniqueEdges.length > 1) {
    return { ok: false, error: COMPOSITE_AMBIGUOUS_EDGE_COPY };
  }
  if (uniqueCameras.length > 1) {
    return { ok: false, error: COMPOSITE_AMBIGUOUS_CAMERA_COPY };
  }
  if (uniqueEdges.length !== 1 || uniqueCameras.length !== 1) {
    return { ok: false, error: COMPOSITE_MISSING_SOURCE_TARGET_COPY };
  }
  return {
    ok: true,
    target: { edge_device_id: uniqueEdges[0], camera_id: uniqueCameras[0] },
  };
}

export function readCompositeTarget(policy: {
  target?: { edge_device_id?: string | null; camera_id?: string | null } | null;
  edge_device_id?: string | null;
  camera_id?: string | null;
} | null | undefined): CompositeDerivedTarget | null {
  const edge = String(policy?.target?.edge_device_id || policy?.edge_device_id || '').trim();
  const camera = String(policy?.target?.camera_id || policy?.camera_id || '').trim();
  if (!edge || !camera) return null;
  return { edge_device_id: edge, camera_id: camera };
}

export function sourcePolicyLocationLabel(
  policy: Pick<Policy, 'scope' | 'camera_id' | 'target'>,
  cameras: Array<Pick<Camera, 'camera_id' | 'name'>> = [],
): string {
  const cameraId = sourcePolicyCameraId(policy);
  const configured = resolveConfiguredCameraLabel(cameraId);
  if (configured && !INTERNAL_ZONE_TOKEN_RE.test(configured)) {
    return configured.replace(/\s+-\s+/g, ' – ');
  }
  const camera = cameras.find((item) => String(item.camera_id || '').trim() === cameraId);
  const cameraName = String(camera?.name || '').trim();
  if (cameraName && !looksLikeRawScopeId(cameraName) && !INTERNAL_ZONE_TOKEN_RE.test(cameraName)) {
    return cameraName.replace(/\s+-\s+/g, ' – ');
  }
  return configured ? configured.replace(/\s+-\s+/g, ' – ') : '';
}

export function sourcePolicyBuyerLabel(
  policy: Policy,
  cameras: Array<Pick<Camera, 'camera_id' | 'name'>> = [],
): string {
  const condition = compositeConditionForPolicyType(policy.policy_type);
  const conditionLabel = condition ? COMPOSITE_CONDITION_BUYER_LABELS[condition] : '';
  const location = sourcePolicyLocationLabel(policy, cameras);
  const name = String(policy.name || '').trim();
  return [name, conditionLabel, location].filter(Boolean).join(' · ');
}

export function compositeSourcePolicies(
  policies: Policy[] | undefined,
  condition?: CompositeConditionType | null,
): Policy[] {
  return (policies || []).filter((policy) => {
    if (!policy || policy.policy_type === COMPOSITE_POLICY_TYPE) return false;
    const mapped = compositeConditionForPolicyType(policy.policy_type);
    if (!mapped) return false;
    if (condition && mapped !== condition) return false;
    return Boolean(String(policy.policy_id || '').trim());
  });
}

export function compositeCameraIdsFromTerms(
  terms: CompositeTerm[],
  policies: Policy[] | undefined,
): string[] {
  const byId = new Map((policies || []).map((policy) => [String(policy.policy_id || '').trim(), policy]));
  const cameras: string[] = [];
  for (const term of terms || []) {
    const policyId = String(term.source_policy_id || '').trim();
    if (!policyId) continue;
    const policy = byId.get(policyId);
    if (!policy) continue;
    const cameraId = sourcePolicyCameraId(policy);
    if (cameraId && !cameras.includes(cameraId)) cameras.push(cameraId);
  }
  return cameras;
}

export function deriveCompositeScopeBinding(rule: CompositeRuleV1, policies?: Policy[]): CompositeScopeBinding {
  const cameras = compositeCameraIdsFromTerms(rule.terms || [], policies);
  if (cameras.length === 1) return 'SAME_CAMERA';
  return rule.scope_binding;
}

export function compositeScopeForSave(
  rule: CompositeRuleV1,
  policies?: Policy[],
  existingScope?: Record<string, any> | null,
): { camera_ids: string[] } | Record<string, never> {
  const fromTerms = compositeCameraIdsFromTerms(rule.terms || [], policies);
  const fromScope = Array.isArray(existingScope?.camera_ids)
    ? (existingScope?.camera_ids as unknown[]).map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const cameraIds = fromTerms.length ? fromTerms : fromScope;
  if (cameraIds.length === 1) return { camera_ids: cameraIds };
  return {};
}

export function compositeLocationLabel(
  rule: CompositeRuleV1,
  policies: Policy[] | undefined,
  cameras: Array<Pick<Camera, 'camera_id' | 'name'>> = [],
): string {
  const cameraIds = compositeCameraIdsFromTerms(rule.terms || [], policies);
  if (cameraIds.length !== 1) return '';
  const policy = (policies || []).find((item) => sourcePolicyCameraId(item) === cameraIds[0]);
  const label = policy ? sourcePolicyLocationLabel(policy, cameras) : resolveConfiguredCameraLabel(cameraIds[0]) || '';
  if (!label || INTERNAL_ZONE_TOKEN_RE.test(label)) return '';
  return label;
}

export function defaultCompositeRule(): CompositeRuleV1 {
  return {
    schema_version: 'composite_rule.v1',
    rule_id: '',
    version: 1,
    name: '',
    enabled: true,
    operator: 'ALL',
    terms: [
      { term_id: 'rae', condition_type: 'RESTRICTED_AREA_ENTRY', min_events: 1 },
      { term_id: 'ppe', condition_type: 'PPE_NON_COMPLIANCE', min_events: 1 },
    ],
    window_seconds: 300,
    scope_binding: 'SAME_ZONE',
    cooldown_seconds: 300,
    severity: 'medium',
    actions: ['email.notify'],
  };
}

export function readCompositeRule(config: Record<string, any> | undefined): CompositeRuleV1 {
  const raw = config && typeof config === 'object' ? config.composite_rule : null;
  const fallback = defaultCompositeRule();
  if (!raw || typeof raw !== 'object') return fallback;
  return {
    schema_version: 'composite_rule.v1',
    rule_id: String(raw.rule_id || ''),
    version: Number.isFinite(Number(raw.version)) ? Number(raw.version) : 1,
    name: String(raw.name || ''),
    enabled: raw.enabled !== false,
    operator: raw.operator === 'ANY' ? 'ANY' : 'ALL',
    terms: Array.isArray(raw.terms) && raw.terms.length
      ? raw.terms.map((term: any, index: number) => {
          const sourcePolicyId = String(term?.source_policy_id || '').trim();
          return {
            term_id: String(term?.term_id || `term_${index + 1}`),
            condition_type: COMPOSITE_CONDITION_TYPES.includes(term?.condition_type)
              ? term.condition_type
              : 'RESTRICTED_AREA_ENTRY',
            min_events: Number.isFinite(Number(term?.min_events)) ? Number(term.min_events) : 1,
            ...(sourcePolicyId ? { source_policy_id: sourcePolicyId } : {}),
          };
        })
      : fallback.terms,
    window_seconds: Number.isFinite(Number(raw.window_seconds)) ? Number(raw.window_seconds) : 300,
    scope_binding:
      raw.scope_binding === 'SAME_CAMERA' || raw.scope_binding === 'SAME_SITE'
        ? raw.scope_binding
        : 'SAME_ZONE',
    cooldown_seconds: Number.isFinite(Number(raw.cooldown_seconds)) ? Number(raw.cooldown_seconds) : 300,
    severity:
      raw.severity === 'low' || raw.severity === 'high' || raw.severity === 'critical'
        ? raw.severity
        : 'medium',
    actions: Array.isArray(raw.actions) && raw.actions.includes('email.notify') ? ['email.notify'] : ['email.notify'],
  };
}

export function canonicalCompositeRulePayload(rule: CompositeRuleV1): CompositeRuleV1 {
  return {
    schema_version: 'composite_rule.v1',
    rule_id: String(rule.rule_id || ''),
    version: Number(rule.version),
    name: String(rule.name || '').trim(),
    enabled: rule.enabled === true,
    operator: rule.operator === 'ANY' ? 'ANY' : 'ALL',
    terms: (rule.terms || []).map((term) => {
      const sourcePolicyId = String(term.source_policy_id || '').trim();
      return {
        term_id: String(term.term_id || '').trim(),
        condition_type: term.condition_type,
        min_events: Number(term.min_events),
        ...(sourcePolicyId ? { source_policy_id: sourcePolicyId } : {}),
      };
    }),
    window_seconds: Number(rule.window_seconds),
    scope_binding: rule.scope_binding,
    cooldown_seconds: Number(rule.cooldown_seconds),
    severity: rule.severity,
    actions: rule.actions?.includes('email.notify') ? ['email.notify'] : [],
  };
}

export function presentCompositeRulePreview(
  rule: CompositeRuleV1,
  options?: { locationLabel?: string | null },
): string {
  const joiner = rule.operator === 'ALL' ? ' AND ' : ' OR ';
  const buyerParts = (rule.terms || []).map((term) => {
    const label = COMPOSITE_PREVIEW_CONDITION_LABELS[term.condition_type] || term.condition_type;
    const count = Number(term.min_events) || 0;
    return count > 1 ? `${count} ${label}` : label;
  });
  const sentenceParts = (rule.terms || []).map((term) => {
    const count = Number(term.min_events) || 0;
    const labels = count === 1 ? COMPOSITE_CONDITION_LABELS_SINGULAR : COMPOSITE_CONDITION_LABELS_PLURAL;
    return `at least ${count} ${labels[term.condition_type] || term.condition_type}`;
  });
  const scope = COMPOSITE_SCOPE_LABELS[rule.scope_binding] || 'configured scope';
  const scopeTitle = COMPOSITE_SCOPE_PREVIEW_LABELS[rule.scope_binding] || 'Configured scope';
  const window = Number(rule.window_seconds) || 0;
  const windowCopy =
    window % 60 === 0
      ? `${window / 60} minute${window / 60 === 1 ? '' : 's'}`
      : `${window} seconds`;
  const name = String(rule.name || '').trim();
  const location = String(options?.locationLabel || '').trim();
  const safeLocation = location && !INTERNAL_ZONE_TOKEN_RE.test(location) ? location : '';
  if (name) {
    return [name, buyerParts.join(joiner), safeLocation, `${scopeTitle} · within ${windowCopy}`]
      .filter(Boolean)
      .join(', ');
  }
  return `Alert when ${sentenceParts.join(joiner)} occur in the ${scope} within ${windowCopy}.`;
}

export function compositeHasForkliftTerm(rule: CompositeRuleV1): boolean {
  return (rule.terms || []).some((term) => term.condition_type === 'PERSON_FORKLIFT_PROXIMITY');
}

export function immediateAlertEnabled(alert: { enabled?: boolean } | undefined | null): boolean {
  return alert?.enabled !== false;
}

export function canonicalImmediatePayload(enabled: boolean): { enabled: boolean } {
  return { enabled };
}

export function canonicalAlertForSave(alert: {
  enabled?: boolean;
  severity?: 'low' | 'medium' | 'high' | 'critical' | string;
  message_template?: string;
} | null | undefined): { enabled: boolean; severity: 'low' | 'medium' | 'high' | 'critical'; message_template?: string } {
  const severity = alert?.severity;
  return {
    enabled: immediateAlertEnabled(alert),
    severity: severity === 'low' || severity === 'high' || severity === 'critical' ? severity : 'medium',
    ...(alert?.message_template != null ? { message_template: alert.message_template } : {}),
  };
}

/** Copied Spatial Ops sentence pattern: "N qualifying occurrences within T". */
export function presentStoredPolicyPatternRule(
  minEvents: number | null | undefined,
  windowSeconds: number | null | undefined,
): string | null {
  if (minEvents == null || windowSeconds == null) return null;
  if (windowSeconds % 60 === 0) {
    const minutes = windowSeconds / 60;
    return `${minEvents} qualifying occurrences within ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${minEvents} qualifying occurrences within ${windowSeconds} seconds`;
}

export const FORKLIFT_PATTERN_COMMISSIONING_COPY =
  'Pattern Alert for forklift-pedestrian proximity is first-class and fail-closed. Production Pattern incidents will not fire until this condition is commissioned.';

function finiteOrNull(value: unknown): number | null {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function effectiveAlertLinesFromForm(formData: any): string[] {
  const immediateOn = immediateAlertEnabled(formData?.alert);
  const patternOn = formData?.pattern?.enabled === true;
  const email = getPolicyActionByType(formData?.actions, 'email.notify');
  const emailConfigured = Boolean(email) && email.enabled !== false;
  const recipientPresent = String(email?.params?.to ?? '').trim().length > 0;
  const mode = String(formData?.config?.operational?.production_ownership_mode || '').trim();
  const needsCommissioning = Boolean(mode) && mode !== 'EPIC400_AUTHORITATIVE';
  const forklift =
    formData?.policy_type === 'forklift_proximity' || formData?.policy_type === 'pedestrian_near_moving_forklift';
  const activity = (on: boolean, failClosed: boolean) =>
    !on ? 'Not active' : failClosed || needsCommissioning ? 'Requires commissioning' : 'Configured';
  const core = patternOn
    ? presentStoredPolicyPatternRule(formData?.pattern?.min_events ?? 5, formData?.pattern?.window_seconds ?? 3600)
    : null;
  const patternCooldown = finiteOrNull(formData?.pattern?.cooldown_seconds);
  const lines = [
    `Immediate: ${immediateOn ? 'ON' : 'OFF'} · ${activity(immediateOn, false)}`,
    `Pattern: ${patternOn ? 'ON' : 'OFF'} · ${activity(patternOn, forklift)}`,
  ];
  if (core) lines.push(`Alert when ${core.replace(' within ', ' occur within ')}`);
  if (patternOn && patternCooldown != null) {
    lines.push(
      `Pattern cooldown: ${
        patternCooldown % 3600 === 0
          ? `${patternCooldown / 3600} hour${patternCooldown / 3600 === 1 ? '' : 's'}`
          : `${patternCooldown} seconds`
      }`,
    );
  }
  lines.push(
    `Email: ${emailConfigured ? 'Configured' : 'Not configured'} · ${recipientPresent ? 'Recipient present' : 'No recipient'}`,
  );
  const cooldown = finiteOrNull(formData?.timing?.cooldown_seconds);
  const dedup = finiteOrNull(formData?.timing?.dedup_window_seconds);
  if (cooldown != null) lines.push(`Notification cooldown: ${cooldown} seconds (not one email per occurrence)`);
  if (dedup != null) lines.push(`Dedup: ${dedup} seconds`);
  return lines;
}
